import React, { memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { HeaderMenuToggle } from '@Actions/navigationActions';
import { logout } from '@Actions/authentication';
import { icons, buttons } from 'seal-ui-components';
import { MenuItem, StyledLink, StyledLinkDesc, StyledLinkName } from './TopNavListItem.style';
import { withTheme } from 'styled-components';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useCopySupportLogs } from '@Hooks/useCopySupportLogs';
import { calculateOverflow, getCurrentPage } from '@Utils/commonUtils';
import mixpanelEvents from '@Mixpanel';

const DefaultListItem = withTheme(
    memo(
        ({ name, to, handleClick, description, withInfo, toggleModal }) => (
            <StyledLink key={name} to={{ pathname: to ? to : '#' }} onClick={handleClick}>
                <StyledLinkName>
                    {name}
                    {withInfo && (
                        <buttons.InsetIconButton
                            size="small"
                            icon={<icons.QuestionmarkSmallIcon />}
                            data-testid={`${name}_link_modal`}
                            onClick={e => {
                                e.preventDefault(); // prevent navigation
                                e.stopPropagation(); // prevent even bubbling to parent

                                toggleModal(to);
                                mixpanelEvents.menuDisplayHelpInformation({
                                    'Analyzer Display Mode': getCurrentPage(),
                                    'Menu help': to
                                });
                            }}
                        />
                    )}
                </StyledLinkName>
                {description && <StyledLinkDesc>{description}</StyledLinkDesc>}
            </StyledLink>
        ),
        (PrevProps, nextProps) => {
            if (JSON.stringify(PrevProps) !== JSON.stringify(nextProps)) return false;

            return true;
        }
    )
);

const CopySupportLogListItem = withTheme(
    memo(
        ({ name, error, errorInfo, handleClick }) => {
            const supportHook = useCopySupportLogs(error, errorInfo);

            const copyAndClose = React.useCallback(e => {
                e.preventDefault();

                supportHook.ieCopy();
                mixpanelEvents.menuOther({
                    'Analyzer Display Mode': getCurrentPage(),
                    'Menu other': 'CopyLogToClipboard'
                });
                handleClick(e);
            }, []);

            return (
                <StyledLink
                    key={name}
                    to={{ pathname: '#' }}
                    onMouseOver={() => supportHook.getTrace()}
                    onFocus={() => supportHook.getTrace()}
                    onClick={copyAndClose}
                >
                    <CopyToClipboard
                        text={supportHook.logText}
                        disabled={supportHook.isCopying}
                        onCopy={() => supportHook.setIsCopying(false)}
                    >
                        <StyledLinkName>{name}</StyledLinkName>
                    </CopyToClipboard>
                </StyledLink>
            );
        },

        (PrevProps, nextProps) => {
            if (JSON.stringify(PrevProps) !== JSON.stringify(nextProps)) return false;

            return true;
        }
    )
);

const TopNavListItem = memo(
    ({ menuItem, toggleModal }) => {
        const dispatch = useDispatch();
        const { user } = useSelector(state => state.auth);
        const isActive = useLocation().pathname.substring(1) === menuItem.to;
        const className = isActive ? 'active' : '';

        const handleClick = React.useCallback(
            () => {
                const currentPage = getCurrentPage();

                calculateOverflow(menuItem.to);
                dispatch(HeaderMenuToggle(false));

                mixpanelEvents.menuPageNavigation({
                    'Analyzer Display Mode': currentPage,
                    'Menu Click': menuItem.to
                });
            },
            [dispatch]
        );

        const logoutHandler = React.useCallback(
            e => {
                e.preventDefault();

                dispatch(HeaderMenuToggle(false));
                dispatch(logout(true, user.username));

                mixpanelEvents.menuOther({
                    'Analyzer Display Mode': getCurrentPage(),
                    'Menu other': 'Logout'
                });
            },
            [dispatch]
        );

        const renderMenuItemSwitch = item => {
            switch (item.name) {
                case 'Settings':
                    return (
                        <MenuItem noBold className={className} key={`${item.name}_link`}>
                            <DefaultListItem
                                data-testid={`${item.name}_link`}
                                name={item.name}
                                to={item.to}
                                handleClick={handleClick}
                            />
                        </MenuItem>
                    );

                case 'Copy support log':
                    return (
                        <MenuItem noBold className={className} key={`${item.name}_link`}>
                            <CopySupportLogListItem
                                data-testid={`${item.name}_link`}
                                name={item.name}
                                handleClick={handleClick}
                            />
                        </MenuItem>
                    );

                case 'Logout':
                    return (
                        <MenuItem className={className} key={`${item.name}_link`}>
                            <DefaultListItem
                                data-testid={`${item.name}_link`}
                                name={item.name}
                                description={item.description}
                                handleClick={logoutHandler}
                            />
                        </MenuItem>
                    );

                default:
                    return (
                        <MenuItem className={className} key={`${item.name}_link`}>
                            <DefaultListItem
                                data-testid={`${item.name}_link`}
                                name={item.name}
                                to={item.to}
                                description={item.description}
                                handleClick={handleClick}
                                withInfo
                                toggleModal={toggleModal}
                            />
                        </MenuItem>
                    );
            }
        };

        return renderMenuItemSwitch(menuItem);
    },
    (PrevProps, nextProps) => {
        if (JSON.stringify(PrevProps) !== JSON.stringify(nextProps)) return false;

        return true;
    }
);

export default TopNavListItem;
